import { WORKSPACE_AGENT_JUMP_TO_BOTTOM_THRESHOLD_PX } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';

/** Slack for "pinned to bottom" before the chat viewport resizes (e.g. composer mounting). */
export const WORKSPACE_AGENT_SCROLL_BOTTOM_TOLERANCE_PX = 4;

export type WorkspaceAgentScrollContainerMetrics = {
  readonly clientHeight: number;
  readonly scrollHeight: number;
  readonly scrollTop: number;
};

/** Viewport metrics plus `scrollTo` — the only DOM surface the pin helper needs. */
export type WorkspaceAgentScrollContainer = WorkspaceAgentScrollContainerMetrics & {
  scrollTo(options?: ScrollToOptions): void;
};

export type PinWorkspaceAgentScrollToBottomResult = {
  didPin: boolean;
  metrics: WorkspaceAgentScrollContainerMetrics;
};

export const getWorkspaceAgentScrollContainerMetrics = (
  container: WorkspaceAgentScrollContainerMetrics,
): WorkspaceAgentScrollContainerMetrics => ({
  clientHeight: container.clientHeight,
  scrollHeight: container.scrollHeight,
  scrollTop: container.scrollTop,
});

export const getIsWorkspaceAgentScrolledToBottom = ({
  clientHeight,
  scrollHeight,
  scrollTop,
}: WorkspaceAgentScrollContainerMetrics): boolean =>
  scrollTop >= scrollHeight - clientHeight - WORKSPACE_AGENT_SCROLL_BOTTOM_TOLERANCE_PX;

export const getWorkspaceAgentMaxScrollTop = ({
  clientHeight,
  scrollHeight,
}: Pick<WorkspaceAgentScrollContainerMetrics, 'clientHeight' | 'scrollHeight'>): number =>
  Math.max(0, scrollHeight - clientHeight);

export const getWorkspaceAgentDistanceFromBottomPx = (metrics: WorkspaceAgentScrollContainerMetrics): number =>
  Math.max(0, metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight);

export const getShouldShowWorkspaceAgentJumpToBottom = (metrics: WorkspaceAgentScrollContainerMetrics): boolean => {
  if (getWorkspaceAgentMaxScrollTop(metrics) <= WORKSPACE_AGENT_SCROLL_BOTTOM_TOLERANCE_PX) {
    return false;
  }

  return getWorkspaceAgentDistanceFromBottomPx(metrics) > WORKSPACE_AGENT_JUMP_TO_BOTTOM_THRESHOLD_PX;
};

export const getShouldPinWorkspaceAgentScrollToBottom = (metrics: WorkspaceAgentScrollContainerMetrics): boolean =>
  getWorkspaceAgentMaxScrollTop(metrics) > metrics.scrollTop + WORKSPACE_AGENT_SCROLL_BOTTOM_TOLERANCE_PX;

export type GetWorkspaceAgentScrollUpwardDeltaPxParams = {
  previousAccumulatedPx: number;
  previousScrollTop: number;
  scrollTop: number;
};

/**
 * Trackpad and touch often move 1–3px per `scroll` event. A single-event threshold would never
 * release; summing consecutive upward moves still ignores compositor jitter that reverses.
 */
export const getWorkspaceAgentScrollUpwardDeltaPx = ({
  previousAccumulatedPx,
  previousScrollTop,
  scrollTop,
}: GetWorkspaceAgentScrollUpwardDeltaPxParams): number => {
  if (scrollTop >= previousScrollTop) {
    return 0;
  }

  return previousAccumulatedPx + (previousScrollTop - scrollTop);
};

export type GetShouldReleaseWorkspaceAgentScrollPinParams = {
  isAtBottom: boolean;
  upwardDeltaPx: number;
};

/**
 * Only a user scrolling *up* should drop stick-to-bottom. Browser clamp, programmatic pins, and
 * content growing below the viewport must not look like the reader left the latest message.
 */
export const getShouldReleaseWorkspaceAgentScrollPin = ({
  isAtBottom,
  upwardDeltaPx,
}: GetShouldReleaseWorkspaceAgentScrollPinParams): boolean =>
  upwardDeltaPx > WORKSPACE_AGENT_SCROLL_BOTTOM_TOLERANCE_PX && !isAtBottom;

export type GetIsWorkspaceAgentScrollPinnedAfterScrollParams = {
  isAtBottom: boolean;
  isProgrammaticPin: boolean;
  upwardDeltaPx: number;
  wasPinned: boolean;
};

/**
 * Next stick-to-bottom flag after a `scroll` event. Programmatic tail pins keep the prior follow
 * state; history prepend restore must not re-arm stick-to-bottom after the reader scrolled up.
 * Only an upward user scroll releases the pin.
 */
export const getIsWorkspaceAgentScrollPinnedAfterScroll = ({
  isAtBottom,
  isProgrammaticPin,
  upwardDeltaPx,
  wasPinned,
}: GetIsWorkspaceAgentScrollPinnedAfterScrollParams): boolean => {
  if (isProgrammaticPin) {
    return wasPinned === true;
  }

  if (getShouldReleaseWorkspaceAgentScrollPin({ isAtBottom, upwardDeltaPx })) {
    return false;
  }

  if (isAtBottom) {
    return true;
  }

  return wasPinned;
};

/**
 * While pinned, disable overflow anchoring so the browser cannot nudge `scrollTop` against our pin
 * (that looks like a jump up ~500ms later as chips reflow). While the reader has scrolled away,
 * keep auto anchoring so thumbnails loading above the viewport do not shove the line they are on.
 */
export const getWorkspaceAgentScrollOverflowAnchor = (isPinned: boolean): 'auto' | 'none' =>
  isPinned ? 'none' : 'auto';

/** Smooth scroll only when new content extends below the viewport — never when re-pinning or scrolling up. */
export const getWorkspaceAgentScrollBehaviorForBottom = (
  metrics: WorkspaceAgentScrollContainerMetrics,
  preferredBehavior: ScrollBehavior,
): ScrollBehavior => {
  if (preferredBehavior === 'smooth' && !getShouldPinWorkspaceAgentScrollToBottom(metrics)) {
    return 'instant';
  }

  return preferredBehavior;
};

/**
 * Pins the chat thread to its latest content. Skips `scrollTo` when already at (or past) the
 * bottom — a no-op call can still restart a smooth animation and look like a jump up.
 */
export const pinWorkspaceAgentScrollToBottom = (
  container: WorkspaceAgentScrollContainer,
  preferredBehavior: ScrollBehavior,
): PinWorkspaceAgentScrollToBottomResult => {
  const metrics = getWorkspaceAgentScrollContainerMetrics(container);

  if (!getShouldPinWorkspaceAgentScrollToBottom(metrics)) {
    return { didPin: false, metrics };
  }

  container.scrollTo({
    top: container.scrollHeight,
    behavior: getWorkspaceAgentScrollBehaviorForBottom(metrics, preferredBehavior),
  });

  return { didPin: true, metrics: getWorkspaceAgentScrollContainerMetrics(container) };
};

export type WorkspaceAgentScrollAnchorSnapshot = {
  scrollHeight: number;
  scrollTop: number;
};

export const captureWorkspaceAgentScrollAnchorSnapshot = (
  container: WorkspaceAgentScrollContainerMetrics,
): WorkspaceAgentScrollAnchorSnapshot => {
  const metrics = getWorkspaceAgentScrollContainerMetrics(container);

  return {
    scrollHeight: metrics.scrollHeight,
    scrollTop: metrics.scrollTop,
  };
};

/** Keeps the same content in view after older rows grow the scroll height above the viewport. */
export const getWorkspaceAgentScrollTopAfterContentPrepended = (
  snapshot: WorkspaceAgentScrollAnchorSnapshot,
  nextScrollHeight: number,
): number => snapshot.scrollTop + (nextScrollHeight - snapshot.scrollHeight);

export type GetWorkspaceAgentHistoryPrependScrollDeltaPxParams = {
  containerScrollHeight: number;
  snapshot: WorkspaceAgentScrollAnchorSnapshot;
  totalSizeAtSnapshot: number;
  virtualizedTotalSize: number;
};

/** Virtualizer growth is the primary signal; DOM scrollHeight is fallback when totalSize has not caught up. */
export const getWorkspaceAgentHistoryPrependScrollDeltaPx = ({
  containerScrollHeight,
  snapshot,
  totalSizeAtSnapshot,
  virtualizedTotalSize,
}: GetWorkspaceAgentHistoryPrependScrollDeltaPxParams): number => {
  const virtualizedSizeDeltaPx = virtualizedTotalSize - totalSizeAtSnapshot;

  if (virtualizedSizeDeltaPx > 0) {
    return virtualizedSizeDeltaPx;
  }

  return Math.max(0, containerScrollHeight - snapshot.scrollHeight);
};

/** After the load-older control unmounts, drop scrollTop so the same rows stay under the viewport. */
export const getWorkspaceAgentScrollTopAfterLoadOlderControlRemoved = (
  scrollTop: number,
  loadOlderControlBlockHeightPx: number,
): number => Math.max(0, scrollTop - loadOlderControlBlockHeightPx);

export type GetWorkspaceAgentScrollTopAfterFinalHistoryPagePrependedParams = {
  loadOlderControlBlockHeightPx: number;
  restoredScrollTop: number;
  scrollTopAtSnapshot: number;
};

/**
 * Final history page: the load-older control sits outside virtualized `totalSize` and unmounts in the
 * same commit as the prepend restore. Shrink scrollTop by the measured control block height.
 */
export const getWorkspaceAgentScrollTopAfterFinalHistoryPagePrepended = ({
  loadOlderControlBlockHeightPx,
  restoredScrollTop,
}: GetWorkspaceAgentScrollTopAfterFinalHistoryPagePrependedParams): number => {
  if (loadOlderControlBlockHeightPx <= 0) {
    return restoredScrollTop;
  }

  return getWorkspaceAgentScrollTopAfterLoadOlderControlRemoved(restoredScrollTop, loadOlderControlBlockHeightPx);
};
